import { useEffect } from 'react';
import { MEDIA, SITE_URL } from '../data/siteData';
import { localizePath, stripLang, useLang } from '../i18n';

const SITE_NAME = 'Speak English Now';
const DEFAULT_TITLE = 'Clases de inglés online y en Barcelona | Speak English Now';
const DEFAULT_DESC = 'Aprende inglés con clases online en España y formación presencial en Barcelona y alrededores. Método práctico de Caroline Franks para adultos, jóvenes y empresas.';
const DEFAULT_IMAGE = `${SITE_URL}${MEDIA.presentationPoster}`;
const DEFAULT_KEYWORDS = 'clases inglés online, aprender inglés, inglés online España, curso inglés, Caroline Franks, centro Cambridge, FCE, CAE, inglés para adultos, inglés para niños';

export default function SEO({ title, description, keywords, canonical, ogImage, imageAlt, ogType = 'website', schema, robots = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1', publishedTime, modifiedTime }) {
  const { lang } = useLang();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullDesc = description || DEFAULT_DESC;
  const fullKeywords = keywords || DEFAULT_KEYWORDS;
  const fullImage = ogImage || DEFAULT_IMAGE;
  const basePath = canonical ? (canonical.replace(SITE_URL, '') || '/') : stripLang(window.location.pathname);
  const canonicalUrl = `${SITE_URL}${lang === 'ca' ? localizePath(basePath, 'ca') : basePath}`;
  const esUrl = `${SITE_URL}${basePath}`;
  const caUrl = `${SITE_URL}${localizePath(basePath, 'ca')}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('name', 'description', fullDesc);
    setMeta('name', 'keywords', fullKeywords);
    setMeta('name', 'robots', robots);
    setMeta('name', 'author', 'Caroline Franks – Speak English Now');

    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', fullDesc);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:image', fullImage);
    setMeta('property', 'og:image:alt', imageAlt || fullTitle);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', lang === 'ca' ? 'ca_ES' : 'es_ES');
    setMeta('property', 'og:url', canonicalUrl);
    if (publishedTime) setMeta('property', 'article:published_time', publishedTime);
    if (modifiedTime) setMeta('property', 'article:modified_time', modifiedTime);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', fullDesc);
    setMeta('name', 'twitter:image', fullImage);
    setMeta('name', 'twitter:image:alt', imageAlt || fullTitle);
    setMeta('name', 'twitter:site', '@SpeakEnglishNow');

    setLink('canonical', canonicalUrl);
    setLink('alternate', esUrl, 'es-ES');
    setLink('alternate', caUrl, 'ca-ES');
    setLink('alternate', esUrl, 'x-default');

    if (schema) {
      let script = document.getElementById('ld-json-page');
      if (!script) {
        script = document.createElement('script');
        script.id = 'ld-json-page';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    } else {
      document.getElementById('ld-json-page')?.remove();
    }

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [fullTitle, fullDesc, fullKeywords, fullImage, imageAlt, ogType, canonicalUrl, esUrl, caUrl, lang, schema, robots, publishedTime, modifiedTime]);

  return null;
}

function setLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (hreflang) link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMeta(attrType, attrValue, content) {
  if (!content) return;
  const selector = `meta[${attrType}="${attrValue}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrType, attrValue);
    document.head.appendChild(el);
  }
  el.content = content;
}
